import React, { useState, useEffect, useCallback } from "react";
import { useApp } from "../../contexts/AppContext";
import { useTheme } from "../../contexts/ThemeContext";
import { TEMPLATE_PLACEHOLDERS, getAllMappings } from "../../services/templateService";
import { getThemeAwareColor, getPriorityConfig } from "../../config/colorConfig";

// Theme-aware group colors - use getGroupColors(theme) to get current theme colors
const getGroupColors = (theme) => ({
  "Contract Details": "#0078d4",
  "Parties":          getThemeAwareColor("gold", theme),
  "Financial":        "#107c10",
  "Addendum":         "#8764b8",
  "Other":            theme.text.secondary,
});

const PlaceholderGuide = () => {
  const { theme } = useTheme();
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState("");
  const [activeGroup, setActiveGroup] = useState("all");

  const groupColors = getGroupColors(theme);

  const groups = [...new Set(TEMPLATE_PLACEHOLDERS.map(p => p.group))];
  const display = activeGroup === "all"
    ? TEMPLATE_PLACEHOLDERS
    : TEMPLATE_PLACEHOLDERS.filter(p => p.group === activeGroup);

  const copy = (token) => {
    navigator.clipboard.writeText(token).then(() => {
      setCopied(token);
      setTimeout(() => setCopied(""), 1800);
    });
  };

  return (
    <div style={{ background: theme.bg.secondary, border: "1px solid "+theme.border, borderRadius: 12, marginBottom: 24, overflow: "hidden" }}>
      {/* Header toggle */}
      <button type="button" onClick={() => setOpen(o => !o)}
        style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 22px", background: "none", border: "none", cursor: "pointer", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ width: 3, height: 20, background: getThemeAwareColor("gold", theme), borderRadius: 2 }} />
          <div style={{ textAlign: "left" }}>
            <div style={{ fontFamily: "'Helvetica Neue',Helvetica,Arial,sans-serif", fontSize: 18, fontWeight: 500, color: theme.text.primary, lineHeight: 1 }}>Placeholder Reference Guide</div>
            <div style={{ fontSize: 15, color: theme.text.secondary, marginTop: 3 }}>
              {TEMPLATE_PLACEHOLDERS.length} tokens available — insert these exactly into your Word document to auto-fill on draft generation
            </div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {!open && (
            <div style={{ display: "flex", gap: 5, flexWrap: "wrap", justifyContent: "flex-end" }}>
              {groups.map(g => (
                <span key={g} style={{ padding: "2px 8px", borderRadius: 10, fontSize: 10, fontWeight: 600, background: (groupColors[g] || theme.text.secondary) + "22", color: groupColors[g] || theme.text.secondary, border: "1px solid " + (groupColors[g] || theme.text.secondary) + "44" }}>{g}</span>
              ))}
            </div>
          )}
          <span style={{ fontSize: 15, color: theme.text.secondary, transform: open ? "rotate(180deg)" : "", transition: "transform 0.2s", display: "inline-block", flexShrink: 0 }}>▼</span>
        </div>
      </button>

      {open && (
        <div style={{ borderTop: "1px solid "+theme.border }}>
          {/* Instructions */}
          <div style={{ padding: "14px 22px", background: theme.bg.tertiary, borderBottom: "1px solid "+theme.border }}>
            <div style={{ fontSize: 15, color: theme.text.secondary, lineHeight: 1.7 }}>
              Type these placeholders <strong style={{ color: getThemeAwareColor("gold", theme) }}>exactly</strong> (case-sensitive, including curly braces) anywhere in your Word template document.
              They will be automatically replaced with the corresponding contract request values when a draft is generated.
              Click any token to copy it.
            </div>
          </div>

          {/* Group filter tabs */}
          <div style={{ padding: "10px 22px", borderBottom: "1px solid "+theme.border, display: "flex", gap: 6, flexWrap: "wrap" }}>
            <button type="button" onClick={() => setActiveGroup("all")}
              style={{ padding: "4px 12px", borderRadius: 20, fontSize: 15, fontWeight: 500, cursor: "pointer", fontFamily: "'Helvetica Neue',Helvetica,Arial,sans-serif", border: "1px solid " + (activeGroup === "all" ? getThemeAwareColor("gold", theme) : theme.border), background: activeGroup === "all" ? getThemeAwareColor("gold", theme) + "22" : "transparent", color: activeGroup === "all" ? getThemeAwareColor("gold", theme) : theme.text.secondary }}>
              All ({TEMPLATE_PLACEHOLDERS.length})
            </button>
            {groups.map(g => {
              const cnt = TEMPLATE_PLACEHOLDERS.filter(p => p.group === g).length;
              const col = groupColors[g] || theme.text.secondary;
              const active = activeGroup === g;
              return (
                <button type="button" key={g} onClick={() => setActiveGroup(g)}
                  style={{ padding: "4px 12px", borderRadius: 20, fontSize: 15, fontWeight: 500, cursor: "pointer", fontFamily: "'Helvetica Neue',Helvetica,Arial,sans-serif", border: "1px solid " + (active ? col : theme.border), background: active ? col + "22" : "transparent", color: active ? col : theme.text.secondary }}>
                  {g} ({cnt})
                </button>
              );
            })}
          </div>

          {/* Token grid */}
          <div style={{ padding: "14px 22px 20px", display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(300px,1fr))", gap: 8 }}>
            {display.map(p => {
              const col = groupColors[p.group] || theme.text.secondary;
              const isCopied = copied === p.token;
              return (
                <div key={p.token}
                  onClick={() => copy(p.token)}
                  title="Click to copy"
                  style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "10px 12px", background: isCopied ? col + "18" : theme.bg.secondary, border: "1px solid " + (isCopied ? col : theme.border), borderRadius: 8, cursor: "pointer", transition: "all 0.15s" }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = col + "66"; e.currentTarget.style.background = col + "0e"; }}
                  onMouseLeave={e => { if (!isCopied) { e.currentTarget.style.borderColor = theme.border; e.currentTarget.style.background = theme.bg.secondary; } }}>
                  <div style={{ width: 3, height: "100%", minHeight: 32, background: col, borderRadius: 2, flexShrink: 0, alignSelf: "stretch" }} />
                  <div style={{ flex: 1, overflow: "hidden" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3 }}>
                      <code style={{ fontFamily: "Courier New,Consolas,monospace", fontSize: 15, color: col, fontWeight: 600, background: col + "18", padding: "1px 5px", borderRadius: 3 }}>
                        {p.token}
                      </code>
                      {isCopied && <span style={{ fontSize: 10, color: col }}>Copied!</span>}
                    </div>
                    <div style={{ fontSize: 15, color: theme.text.primary, fontWeight: 500, marginBottom: 2 }}>{p.label}</div>
                    <div style={{ fontSize: 15, color: theme.text.secondary }}>{p.source}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const TEMPLATE_DESCRIPTIONS = {
  "NDA_Template": "Non-Disclosure Agreement template for confidentiality arrangements",
  "Standard_Template_for_Service": "Standard service agreement template",
  "Standard_Template_for_Goods": "Standard procurement/goods agreement template",
  "LOGIC_Template_for_Service": "LOGIC service agreement template",
  "LOGIC_GOODS_TEMPLATE": "LOGIC goods and procurement template",
  "Consultancy_Service_Agreement": "Consultancy and professional services agreement",
  "Contract_Review_and_Approval_Form": "CRAF - Contract review and approval form",
  "Addendum_Template": "Contract addendum and amendment template",
};

// ─── Template Mappings panel ──────────────────────────────────────────────────

const MappingModal = ({ mapping, templateFiles, onClose, onSave, saving, contractTypes }) => {
  const { theme } = useTheme();
  const isEdit = !!mapping?.id;
  const [form, setForm] = useState({
    Title: mapping?.Title || "",
    TemplateFileName: mapping?.TemplateFileName || "",
    IsActive: mapping?.IsActive || "Yes",
  });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const modalInp = { width: "100%", padding: "10px 12px", background: theme.bg.secondary, border: "1px solid "+theme.border, borderRadius: 8, color: theme.text.primary, fontSize: 15, fontFamily: "'Helvetica Neue',Helvetica,Arial,sans-serif", outline: "none" };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.78)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 600 }}>
      <div style={{ background: theme.bg.secondary, border: "1px solid "+theme.border, borderRadius: 16, padding: 32, width: 500, maxWidth: "95vw", boxShadow: "0 24px 64px rgba(0,0,0,.65)", position: "relative" }}>
        <div style={{ position: "absolute", top: 0, left: "10%", right: "10%", height: 2, background: `linear-gradient(90deg,transparent,${getThemeAwareColor("gold", theme)},transparent)` }} />
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
          <h3 style={{ fontFamily: "'Helvetica Neue',Helvetica,Arial,sans-serif", fontSize: 22, fontWeight: 500, color: theme.text.primary, margin: 0 }}>
            {isEdit ? "Edit Mapping" : "Add Template Mapping"}
          </h3>
          <button onClick={onClose} style={{ background: "none", border: "none", color: theme.text.secondary, cursor: "pointer", fontSize: 22 }}>×</button>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: "block", fontSize: 15, color: theme.text.tertiary, marginBottom: 6, fontWeight: 500 }}>Contract Template <span style={{ color: getThemeAwareColor("gold", theme) }}>*</span></label>
          <select value={form.Title} onChange={e => set("Title", e.target.value)} style={modalInp}
            onFocus={e => e.target.style.borderColor = getThemeAwareColor("gold", theme)} onBlur={e => e.target.style.borderColor = theme.border}>
            <option value="">Select contract template...</option>
            {contractTypes.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: "block", fontSize: 15, color: theme.text.tertiary, marginBottom: 6, fontWeight: 500 }}>Template File <span style={{ color: getThemeAwareColor("gold", theme) }}>*</span></label>
          <select value={form.TemplateFileName} onChange={e => set("TemplateFileName", e.target.value)} style={modalInp}
            onFocus={e => e.target.style.borderColor = getThemeAwareColor("gold", theme)} onBlur={e => e.target.style.borderColor = theme.border}>
            <option value="">Select uploaded template...</option>
            {templateFiles.map(f => <option key={f.name} value={f.name}>{f.name.replace(/_/g, " ").replace(/\.(docx?|pdf)$/i, "")}</option>)}
          </select>
          {templateFiles.length === 0 && (
            <div style={{ fontSize: 15, color: "#ca5010", marginTop: 4 }}>No templates uploaded yet. Upload a .docx file first.</div>
          )}
        </div>

        <div style={{ marginBottom: 24 }}>
          <label style={{ display: "block", fontSize: 15, color: theme.text.tertiary, marginBottom: 8, fontWeight: 500 }}>Status</label>
          <div style={{ display: "flex", gap: 16 }}>
            {["Yes", "No"].map(v => (
              <label key={v} style={{ display: "flex", alignItems: "center", gap: 6, color: theme.text.tertiary, cursor: "pointer", fontSize: 15 }}>
                <input type="radio" name="isActive" value={v} checked={form.IsActive === v} onChange={() => set("IsActive", v)} style={{ accentColor: getThemeAwareColor("gold", theme) }} />
                {v === "Yes" ? "Active" : "Inactive"}
              </label>
            ))}
          </div>
        </div>

        <div style={{ background: theme.bg.secondary, borderRadius: 8, padding: "10px 14px", marginBottom: 20, fontSize: 15, color: theme.text.secondary, lineHeight: 1.6 }}>
          When a <strong style={{ color: getThemeAwareColor("gold", theme) }}>{form.Title || "…"}</strong> contract is submitted,
          the system will auto-generate a draft from{" "}
          <strong style={{ color: getThemeAwareColor("gold", theme) }}>{form.TemplateFileName || "…"}</strong> and place it in the contract folder.
        </div>

        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ padding: "10px 20px", background: "transparent", border: "1px solid "+theme.border, borderRadius: 8, color: theme.text.secondary, cursor: "pointer", fontFamily: "'Helvetica Neue',Helvetica,Arial,sans-serif" }}>Cancel</button>
          <button onClick={() => onSave(form, mapping?.id)} disabled={!form.Title || !form.TemplateFileName || saving}
            style={{ padding: "10px 24px", background: form.Title && form.TemplateFileName ? getThemeAwareColor("gold", theme) : theme.bg.tertiary, border: "none", borderRadius: 8, color: form.Title && form.TemplateFileName ? ("#fff") : theme.text.secondary, cursor: form.Title && form.TemplateFileName ? "pointer" : "not-allowed", fontFamily: "'Helvetica Neue',Helvetica,Arial,sans-serif", fontWeight: 600 }}>
            {saving ? "Saving..." : isEdit ? "Save Changes" : "Add Mapping"}
          </button>
        </div>
      </div>
    </div>
  );
};

const TemplateMappingsPanel = ({ service, addNotification, isAdmin, templateFiles, contractTypes }) => {
  const { theme } = useTheme();
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [saving, setSaving] = useState(false);
  const groupColors = getGroupColors(theme);

  const load = useCallback(() => {
    setLoading(true);
    getAllMappings(service)
      .then(setMappings)
      .catch(() => setMappings([]))
      .finally(() => setLoading(false));
  }, [service]);

  useEffect(() => { load(); }, []);

  const handleSave = async (fields, id) => {
    setSaving(true);
    try {
      if (id) {
        await service.updateListItem("TemplateMappings", id, fields);
        addNotification("Mapping updated.", "success");
      } else {
        await service.createListItem("TemplateMappings", fields);
        addNotification("Mapping added.", "success");
      }
      setModal(null);
      load();
    } catch (e) {
      addNotification("Error saving mapping: " + e.message, "error");
    } finally { setSaving(false); }
  };

  const handleDelete = async (m) => {
    if (!window.confirm(`Remove mapping for "${m.Title}"?`)) return;
    try {
      await service.deleteListItem("TemplateMappings", m.id);
      addNotification("Mapping removed.", "success");
      load();
    } catch (e) { addNotification("Error: " + e.message, "error"); }
  };

  // Highlight contract types that have no active mapping
  const mappedTypes = new Set(mappings.filter(m => m.IsActive === "Yes").map(m => m.Title));
  const unmapped = contractTypes.filter(t => !mappedTypes.has(t));

  return (
    <div style={{ background: theme.bg.secondary, border: "1px solid "+theme.border, borderRadius: 12, marginBottom: 24, overflow: "hidden" }}>
      {/* Header */}
      <div style={{ padding: "16px 22px", background: theme.bg.tertiary, borderBottom: "1px solid "+theme.border, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ width: 3, height: 20, background: getThemeAwareColor("gold", theme), borderRadius: 2 }} />
          <div>
            <div style={{ fontFamily: "'Helvetica Neue',Helvetica,Arial,sans-serif", fontSize: 18, fontWeight: 500, color: theme.text.primary, lineHeight: 1 }}>Template Mappings</div>
            <div style={{ fontSize: 15, color: theme.text.secondary, marginTop: 3 }}>
              {mappings.filter(m => m.IsActive === "Yes").length} active · {unmapped.length} contract type{unmapped.length !== 1 ? "s" : ""} unmapped
            </div>
          </div>
        </div>
        {isAdmin && (
          <button onClick={() => setModal({ mapping: {} })}
            style={{ padding: "8px 16px", background: getThemeAwareColor("gold", theme), border: "none", borderRadius: 8, color: "#fff", cursor: "pointer", fontSize: 15, fontFamily: "'Helvetica Neue',Helvetica,Arial,sans-serif", fontWeight: 600, whiteSpace: "nowrap" }}>
            + Add Mapping
          </button>
        )}
      </div>

      {/* Unmapped warning */}
      {unmapped.length > 0 && (
        <div style={{ padding: "10px 22px", background: "#ca50100a", borderBottom: "1px solid #ca501022", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <span style={{ fontSize: 15, color: "#ca5010", fontWeight: 500 }}>No active mapping:</span>
          {unmapped.map(t => (
            <span key={t} style={{ padding: "2px 8px", borderRadius: 4, fontSize: 15, background: "#ca501022", color: "#ca5010", border: "1px solid #ca501044" }}>{t}</span>
          ))}
          <span style={{ fontSize: 15, color: theme.text.secondary }}>— drafts will not be auto-generated for these types.</span>
        </div>
      )}

      {/* Mappings table */}
      {loading ? (
        <div style={{ padding: 32, textAlign: "center", color: theme.text.secondary }}>Loading mappings...</div>
      ) : mappings.length === 0 ? (
        <div style={{ padding: "28px 22px", textAlign: "center", color: theme.text.secondary, fontSize: 15 }}>
          No mappings yet. {isAdmin ? 'Click "+ Add Mapping" to link a contract type to a template.' : ""}
        </div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 15 }}>
            <thead>
              <tr style={{ background: theme.bg.tertiary }}>
                {["Contract Template", "Template File", "Status", ...(isAdmin ? ["Actions"] : [])].map(h => (
                  <th key={h} style={{ padding: "10px 20px", textAlign: "left", fontSize: 15, fontWeight: 500, color: theme.text.secondary, textTransform: "uppercase", letterSpacing: "0.06em", borderBottom: "1px solid "+theme.border, whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {mappings.map((m, i) => (
                <tr key={m.id} style={{ borderBottom: "1px solid "+theme.border }}
                  onMouseEnter={e => Array.from(e.currentTarget.cells).forEach(td => td.style.background = getThemeAwareColor("gold", theme) + "06")}
                  onMouseLeave={e => Array.from(e.currentTarget.cells).forEach(td => td.style.background = "")}>
                  <td style={{ padding: "12px 20px" }}>
                    <span style={{ fontWeight: 600, color: theme.text.primary }}>{m.Title}</span>
                  </td>
                  <td style={{ padding: "12px 20px" }}>
                    <span style={{ fontFamily: "Courier New,Consolas,monospace", fontSize: 15, color: getThemeAwareColor("gold", theme) }}>{m.TemplateFileName}</span>
                  </td>
                  <td style={{ padding: "12px 20px" }}>
                    <span style={{ padding: "3px 10px", borderRadius: 20, fontSize: 15, fontWeight: 500, background: m.IsActive === "Yes" ? "#107c1022" : "#79777522", color: m.IsActive === "Yes" ? "#107c10" : "#797775" }}>
                      {m.IsActive === "Yes" ? "Active" : "Inactive"}
                    </span>
                  </td>
                  {isAdmin && (
                    <td style={{ padding: "12px 20px" }}>
                      <div style={{ display: "flex", gap: 6 }}>
                        <button onClick={() => setModal({ mapping: m })}
                          style={{ padding: "5px 12px", background: getThemeAwareColor("gold", theme) + "22", border: `1px solid ${getThemeAwareColor("gold", theme)}44`, borderRadius: 6, color: getThemeAwareColor("gold", theme), cursor: "pointer", fontSize: 15, fontFamily: "'Helvetica Neue',Helvetica,Arial,sans-serif" }}>
                          Edit
                        </button>
                        <button onClick={() => handleDelete(m)}
                          style={{ padding: "5px 10px", background: "transparent", border: `1px solid ${theme.border}`, borderRadius: 6, color: theme.text.secondary, cursor: "pointer", fontSize: 15 }}>
                          Remove
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <MappingModal
          mapping={modal.mapping}
          templateFiles={templateFiles}
          onClose={() => setModal(null)}
          onSave={handleSave}
          saving={saving}
          contractTypes={contractTypes}
        />
      )}
    </div>
  );
};

export const TemplateLibrary = () => {
  const { theme } = useTheme();
  const { service, addNotification, isAdmin, contractTypes } = useApp();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [driveId, setDriveId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    service.getDrives().then(d => {
      const drive = d?.value?.find(dr=>dr.name==="ContractTemplates"||dr.name==="ContractDocuments");
      if(drive) {
        setDriveId(drive.id);
        return service.getFolderContents(drive.id, "Templates");
      }
      return { value: [] };
    }).then(d => {
      setTemplates(d?.value?.filter(i=>!i.folder) || []);
    }).catch(()=>{
      // Fallback: show hardcoded templates
      setTemplates([
        {name:"NDA_Template.docx",webUrl:"#"},
        {name:"Standard_Template_for_Service.docx",webUrl:"#"},
        {name:"Standard_Template_for_Goods.docx",webUrl:"#"},
        {name:"LOGIC_Template_for_Service.docx",webUrl:"#"},
        {name:"LOGIC_GOODS_TEMPLATE.docx",webUrl:"#"},
        {name:"Consultancy_Service_Agreement.docx",webUrl:"#"},
        {name:"Contract_Review_and_Approval_Form_CRAF.docx",webUrl:"#"},
        {name:"Addendum_Template.docx",webUrl:"#"},
      ]);
    }).finally(()=>setLoading(false));
  }, []);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    if(!files.length || !driveId) return;

    // Validate all files are .docx
    const invalidFiles = files.filter(f => !f.name.toLowerCase().endsWith(".docx"));
    if(invalidFiles.length > 0) {
      addNotification(`Invalid file type(s): ${invalidFiles.map(f=>f.name).join(", ")}. Only .docx files are allowed.`, "error");
      return;
    }

    setUploading(true);
    try {
      for(const file of files) {
        const buf = await file.arrayBuffer();
        const safeName = file.name.replace(/ /g, "_");
        await service.uploadFile(driveId, "Templates", safeName, buf);
      }
      const d = await service.getFolderContents(driveId, "Templates");
      setTemplates(d?.value?.filter(i=>!i.folder) || []);
      addNotification("Template(s) uploaded successfully","success");
    } catch(ex) { addNotification("Upload failed: "+ex.message,"error"); }
    setUploading(false);
  };

  const handleDelete = async (templateId, templateName) => {
    if (!window.confirm(`Delete template "${templateName}"? This cannot be undone.`)) return;

    setUploading(true);
    try {
      await service.deleteFile(driveId, templateId);
      const d = await service.getFolderContents(driveId, "Templates");
      setTemplates(d?.value?.filter(i=>!i.folder) || []);
      addNotification("Template deleted successfully","success");
    } catch(ex) {
      addNotification("Failed to delete template: "+ex.message,"error");
    }
    setUploading(false);
  };

  const getIcon = (name) => {
    if(name.includes("NDA")) return "🤝";
    if(name.includes("Service")||name.includes("Consultancy")) return "🔧";
    if(name.includes("Goods")||name.includes("Procurement")) return "📦";
    if(name.includes("CRAF")) return "✅";
    if(name.includes("Addendum")) return "📌";
    return "📄";
  };

  const getDesc = (name) => {
    const key = Object.keys(TEMPLATE_DESCRIPTIONS).find(k=>name.includes(k));
    return key ? TEMPLATE_DESCRIPTIONS[key] : "Contract template document";
  };

  const filtered = templates.filter(t => !search || t.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div>
      <div style={{marginBottom:28}}>
        <h1 style={{fontFamily:"'Helvetica Neue',Helvetica,Arial,sans-serif",fontSize:32,fontWeight:500,color:theme.text.primary,margin:"0 0 4px"}}>Template Library</h1>
        <p style={{color:theme.text.secondary,margin:0}}>Approved contract templates. Open to edit a copy in SharePoint.</p>
        <div style={{height:2,background:`linear-gradient(90deg,${getThemeAwareColor("gold", theme)},transparent)`,margin:"8px 0 0"}}/>
      </div>

      <div style={{display:"flex",gap:10,marginBottom:24}}>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search templates..." style={{flex:1,padding:"10px 14px",background:theme.bg.secondary,border:"1px solid "+theme.border,borderRadius:8,color:theme.text.primary,fontSize:15,fontFamily:"'Helvetica Neue',Helvetica,Arial,sans-serif",outline:"none"}}/>
        {isAdmin && (
          <label style={{padding:"10px 20px",background:getThemeAwareColor("gold", theme),border:"none",borderRadius:8,color:"#fff",cursor:"pointer",fontSize:15,fontFamily:"'Helvetica Neue',Helvetica,Arial,sans-serif",fontWeight:600,whiteSpace:"nowrap",display:"inline-block"}}>
            {uploading ? "Uploading..." : "+ Upload Template"}
            <input type="file" multiple accept=".docx" style={{display:"none"}} onChange={handleUpload}/>
          </label>
        )}
      </div>

      <TemplateMappingsPanel service={service} addNotification={addNotification} isAdmin={isAdmin} templateFiles={templates} contractTypes={contractTypes} />
      <PlaceholderGuide />

      {loading ? (
        <div style={{textAlign:"center",padding:64,color:theme.text.secondary}}>Loading templates...</div>
      ) : (
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))",gap:16}}>
          {filtered.map((t,i) => (
            <div key={i} style={{background:theme.bg.secondary,border:"1px solid "+theme.border,borderRadius:12,padding:24,position:"relative",overflow:"hidden",transition:"border-color 0.2s"}}
              onMouseEnter={e=>e.currentTarget.style.borderColor=getThemeAwareColor("gold", theme)+"44"}
              onMouseLeave={e=>e.currentTarget.style.borderColor=theme.border}>
              <div style={{position:"absolute",top:0,left:0,right:0,height:2,background:`linear-gradient(90deg,${getThemeAwareColor("gold", theme)}44,transparent)`}}/>
              <div style={{fontSize:32,marginBottom:12}}>{getIcon(t.name)}</div>
              <div style={{fontSize:15,fontWeight:600,color:theme.text.primary,marginBottom:6,wordBreak:"break-word"}}>{t.name.replace(/.docx|.pdf/,"").replace(/_/g," ")}</div>
              <p style={{fontSize:15,color:theme.text.secondary,lineHeight:1.5,marginBottom:16}}>{getDesc(t.name)}</p>
              <div style={{display:"flex",gap:8}}>
                <a href={t.webUrl} target="_blank" rel="noreferrer" style={{flex:1,padding:"8px 12px",background:theme.bg.dark,border:"1px solid "+theme.border,borderRadius:6,color:getThemeAwareColor("gold", theme),cursor:"pointer",fontSize:15,fontFamily:"'Helvetica Neue',Helvetica,Arial,sans-serif",fontWeight:500,textAlign:"center",textDecoration:"none",display:"block"}}>
                  Open in SharePoint ↗
                </a>
                {isAdmin && (
                  <button onClick={() => handleDelete(t.id, t.name)} disabled={uploading} style={{padding:"8px 12px",background:"transparent",border:"1px solid #a4262c",borderRadius:6,color:"#a4262c",cursor:uploading?"not-allowed":"pointer",fontSize:13,fontFamily:"'Helvetica Neue',Helvetica,Arial,sans-serif",fontWeight:600,opacity:uploading?0.6:1,transition:"all 0.2s"}}>
                    🗑️ Delete
                  </button>
                )}
              </div>
            </div>
          ))}
          {filtered.length === 0 && (
            <div style={{gridColumn:"1/-1",textAlign:"center",padding:64,color:theme.text.secondary}}>
              <div style={{fontSize:48,marginBottom:12}}>📂</div>
              <div>No templates found. {isAdmin?"Upload your first template above.":""}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
